import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import { trackToolClick } from '../../../lib/trackToolClick';
import { toastError, errorMessage } from '../../../lib/toast';
import { CheckIcon, CloseIcon, MinusIcon } from '../icons';

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal drawer behaviour: focus the close button on open, keep Tab inside the
 * drawer, close on Escape, lock page scroll, and hand focus back to whatever
 * opened it (the tool card) on unmount.
 */
function useModalDrawer(drawerRef, closeBtnRef, onClose) {
  useEffect(() => {
    const opener = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtnRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [drawerRef, closeBtnRef]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab' || !drawerRef.current) return;
      const nodes = Array.from(drawerRef.current.querySelectorAll(FOCUSABLE));
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const inside = drawerRef.current.contains(document.activeElement);
      if (e.shiftKey && (document.activeElement === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerRef, onClose]);
}

/**
 * Side drawer: full member access list for one tool + a bulk "request for team"
 * action. Reuses GET /api/dept/:id/members and POST /api/dept/:id/request-tool.
 */
export default function ToolDrawer({ deptId, toolId, tool, onClose, onRequestSubmitted }) {
  const [members, setMembers] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState('');
  const [requestOpen, setRequestOpen] = useState(false);
  const closeBtnRef = useRef(null);
  const drawerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);
  useModalDrawer(drawerRef, closeBtnRef, onClose);

  const loadMembers = useCallback(() => {
    let active = true;
    setMembers(null);
    setError('');
    axios
      .get(`${API_BASE_URL}/api/dept/${deptId}/members`, {
        params: { tool_id: toolId },
        withCredentials: true,
      })
      .then((r) => { if (active) setMembers(r.data.members || []); })
      .catch(() => { if (active) setError("Couldn't load the member access list."); });
    return () => { active = false; };
  }, [deptId, toolId]);

  useEffect(() => loadMembers(), [loadMembers]);

  const missing = (members || []).filter((m) => !m.has_access);
  const withAccess = (members || []).filter((m) => m.has_access);

  const submitRequest = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/dept/${deptId}/request-tool`,
        { tool_id: toolId, member_ids: missing.map((m) => m.id), reason: reason.trim() },
        { withCredentials: true }
      );
      // onRequestSubmitted unmounts this drawer (parent clears the open tool),
      // so guard the post-await state updates against an unmounted component.
      onRequestSubmitted(r.data.member_count ?? missing.length);
    } catch (err) {
      toastError(errorMessage(err, "Couldn't submit the request. Please try again."));
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  return (
    <>
      <div className="dept2-drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <aside ref={drawerRef} className="dept2-drawer" role="dialog" aria-modal="true" aria-labelledby="dept2-drawer-title">
        <header className="dept2-drawer__header">
          <div>
            <p className="dept2-eyebrow">Tool access</p>
            <h2 id="dept2-drawer-title" className="dept2-drawer__title">{tool?.name}</h2>
            {tool?.category_name && <p className="dept2-drawer__cat">{tool.category_name}</p>}
          </div>
          <button ref={closeBtnRef} type="button" className="dept2-icon-btn" onClick={onClose} aria-label="Close drawer">
            <CloseIcon />
          </button>
        </header>

        {tool?.link && (
          <a className="btn btn-secondary btn-small dept2-drawer__link" href={tool.link} target="_blank" rel="noreferrer" onClick={() => trackToolClick(toolId)}>
            Open {tool.name}
          </a>
        )}

        {error && (
          <div className="dept2-error" role="alert">
            <p>{error}</p>
            <button type="button" className="btn btn-secondary btn-small" onClick={loadMembers}>Try again</button>
          </div>
        )}
        {!error && members === null && <p className="dept2-hint" role="status" aria-busy="true">Loading members…</p>}

        {!error && members && (
          <>
            <div className="dept2-drawer__summary">
              <div><strong>{withAccess.length}</strong><span>have access</span></div>
              <div><strong>{missing.length}</strong><span>missing</span></div>
            </div>

            {missing.length > 0 && !requestOpen && (
              <button type="button" className="btn btn-primary dept2-block" onClick={() => setRequestOpen(true)}>
                Request access for team ({missing.length} missing)
              </button>
            )}

            {requestOpen && (
              <div className="dept2-request-box">
                <label htmlFor="dept2-reason">Reason (optional)</label>
                <textarea
                  id="dept2-reason"
                  maxLength={500}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why does the team need this tool?"
                />
                <div className="dept2-request-box__actions">
                  <button type="button" className="btn btn-secondary btn-small" onClick={() => setRequestOpen(false)} disabled={submitting}>Cancel</button>
                  <button type="button" className="btn btn-primary btn-small" onClick={submitRequest} disabled={submitting} aria-busy={submitting}>
                    {submitting ? 'Submitting…' : `Request for ${missing.length} member${missing.length === 1 ? '' : 's'}`}
                  </button>
                </div>
              </div>
            )}

            <h3 className="dept2-section-heading">Members</h3>
            <ul className="dept2-member-list">
              {members.map((m) => (
                <li key={m.id} className={m.has_access ? 'has' : 'missing'}>
                  <div className="who"><strong>{m.name}</strong><small>{m.email}</small></div>
                  <span className={m.has_access ? 'dept2-flag dept2-flag--has' : 'dept2-flag'}>
                    {m.has_access ? <CheckIcon /> : <MinusIcon />}
                    {m.has_access ? 'Has access' : 'No access'}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </aside>
    </>
  );
}
